import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { Search as SearchIcon, Place as PlaceIcon, MyLocation as MyLocationIcon } from '@mui/icons-material';
import { validZonePoint, ZonePoint } from '../utils/geofenceGeometry';

interface AddressResult { id: string; label: string; point: ZonePoint }
interface Props {
  disabled: boolean;
  getProximity: () => ZonePoint;
  onSelect: (point: ZonePoint) => void;
  circular: boolean;
}

const GeofenceAddressSearch: React.FC<Props> = ({ disabled, getProximity, onSelect, circular }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AddressResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const locationRequest = useRef(0);
  useEffect(() => () => { locationRequest.current += 1; }, []);
  const request = useRef<AbortController | null>(null);
  const selectedAddress = useRef('');
  const proximity = useRef(getProximity);
  proximity.current = getProximity;

  useEffect(() => {
    const text = query.trim();
    if (selectedAddress.current === query) return;
    const debounce = window.setTimeout(() => { void search(); }, 350);
    const search = async () => {
      const token = process.env.REACT_APP_MAPBOX_TOKEN;
      if (disabled || text.length < 3 || !token) return;
      request.current?.abort();
      const controller = new AbortController(); request.current = controller;
      setLoading(true); setError(''); setSearched(false); setResults([]);
      const timeout = window.setTimeout(() => controller.abort(), 12000);
      try {
        const params = new URLSearchParams({ q: text, access_token: token, limit: '5', autocomplete: 'true', language: 'en', permanent: 'true' });
        const point = proximity.current();
        if (validZonePoint(point)) params.set('proximity', point.join(','));
        // Selected coordinates can become a saved geofence, so request permanent geocoding.
        const response = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? 'Address search is unavailable with the current map configuration. You can still place a pin on the map.' : 'Address search could not connect. Please try again.');
        const body = await response.json();
        if (request.current !== controller || controller.signal.aborted) return;
        const matches: AddressResult[] = (Array.isArray(body.features) ? body.features : []).flatMap((feature: any) => {
          const coordinates = feature.geometry?.coordinates;
          const label = feature.properties?.full_address || [feature.properties?.name, feature.properties?.place_formatted].filter(Boolean).join(', ');
          if (feature.geometry?.type !== 'Point' || !Array.isArray(coordinates) || coordinates.length !== 2 || !validZonePoint(coordinates as ZonePoint) || !label) return [];
          return [{ id: feature.id || feature.properties?.mapbox_id || coordinates.join(','), label, point: coordinates as ZonePoint }];
        });
        setResults(matches); setSearched(true);
      } catch (failure) {
        if (request.current !== controller) return;
        setError(controller.signal.aborted ? 'Address search timed out. Please try again.' : failure instanceof Error ? failure.message : 'Address search failed. Please try again.');
      } finally {
        window.clearTimeout(timeout);
        if (request.current === controller) setLoading(false);
      }
    };
    return () => {
      window.clearTimeout(debounce);
      request.current?.abort(); request.current = null;
    };
  }, [query, disabled]);

  const locate = () => {
    if (disabled || locating) return;
    request.current?.abort(); request.current = null;
    selectedAddress.current = '';
    setQuery(''); setResults([]); setSearched(false); setLoading(false); setError(''); setLocationMessage('');
    if (!navigator.geolocation) {
      setError('Your browser does not support location detection. Search for an address or place a pin on the map.');
      return;
    }
    const id = ++locationRequest.current;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(position => {
      if (locationRequest.current !== id) return;
      setLocating(false);
      const point: ZonePoint = [position.coords.longitude, position.coords.latitude];
      if (!validZonePoint(point)) { setError('Your location could not be determined. Try searching for an address.'); return; }
      onSelect(point);
      const accuracy = position.coords.accuracy;
      setLocationMessage(`Current location selected${Number.isFinite(accuracy) && accuracy >= 0 ? ` · reported accuracy: approximately ${Math.ceil(accuracy)} m` : ''}. Check the map and refine the pin or boundary if needed.`);
    }, failure => {
      if (locationRequest.current !== id) return;
      setLocating(false);
      setError(failure.code === 1
        ? 'Location permission was denied. Allow location access in your browser, or search for an address.'
        : failure.code === 3 ? 'Location detection timed out. Try again or search for an address.'
        : 'Your current location is unavailable. Try again or search for an address.');
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  return <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { sm: 'flex-start' } }}>
      <TextField fullWidth size="small" label="Search address or area" placeholder="e.g., Adeola Odeku Street, Victoria Island, Lagos" value={query} disabled={disabled || locating}
        inputProps={{ maxLength: 256 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>, endAdornment: loading ? <InputAdornment position="end"><CircularProgress size={18} aria-label="Searching addresses" /></InputAdornment> : undefined }}
        onChange={event => {
          setLocationMessage('');
          selectedAddress.current = '';
          request.current?.abort(); request.current = null;
          setQuery(event.target.value); setResults([]); setSearched(false); setError(''); setLoading(false);
        }}
        onKeyDown={event => { if (event.key === 'Enter') event.preventDefault(); }} />
      <Button type="button" variant="outlined" onClick={locate} disabled={disabled || locating}
        startIcon={locating ? <CircularProgress size={16} color="inherit" /> : <MyLocationIcon />}
        sx={{ minHeight: 40, flexShrink: 0, whiteSpace: 'nowrap' }}>
        {locating ? 'Locating…' : 'Use my location'}
      </Button>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
      {circular ? 'Choose a matching address to place the center pin, then adjust the radius.' : 'Choose a matching address to move the map, then draw the boundary around your area.'}
    </Typography>
    {locationMessage && <Alert severity="info" sx={{ mt: 1 }}>{locationMessage}</Alert>}
    {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
    {searched && !results.length && <Alert severity="info" sx={{ mt: 1 }}>No matching addresses found. Include the city or try a nearby street or area.</Alert>}
    {!!results.length && <List aria-label="Matching addresses" sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', maxHeight: 240, overflow: 'auto' }}>
      {results.map(result => <ListItemButton key={result.id} onClick={() => { selectedAddress.current = result.label; request.current?.abort(); request.current = null; setLoading(false); onSelect(result.point); setQuery(result.label); setResults([]); setSearched(false); }}>
        <PlaceIcon color="primary" sx={{ mr: 1, flexShrink: 0 }} /><ListItemText primary={result.label} primaryTypographyProps={{ variant: 'body2' }} />
      </ListItemButton>)}
    </List>}
  </Box>;
};
export default GeofenceAddressSearch;
